import { Button, InlineLoading, Modal } from '@wfp/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fileNameMap, publicationUploadMaxSize } from 'src/constants';
import { receiveAPIResponseHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { PDF_MERGE_LIST, REPORT_LIST_DATA_KEY } from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import { getActionAPISource } from 'src/utils';
import { calcMergeStatus, showNotification } from 'src/utils';
import ACRUploadErrorMessage from '../PublicationStatusTab/ACRUploadErrorMessage';
import ACRUploadNotificationError from '../PublicationStatusTab/ACRUploadNotificationError';

interface Props {
  period?: string | number;
}

interface FilesSelectedError {
  error: string;
  filename: string;
}

export interface FileValidationContext {
  projectsCodesList: any[];
  doneProjectsCodesList: any[];
}

export function validateFile(
  file: File,
  { projectsCodesList, doneProjectsCodesList }: FileValidationContext,
): string {
  const fileFormat = file?.name?.split('.').pop();
  const fileName = file?.name?.split('.').shift();

  if (fileFormat !== 'pdf') return 'file_type';

  if (file?.size / 1024 / 1024 > publicationUploadMaxSize) return 'file_size';

  if (!fileName.includes(' ')) return 'file_name';

  const fileNameSplitted = fileName.split(' ');
  if (!projectsCodesList.includes(fileNameSplitted[0])) {
    return 'file_name_project_id';
  }

  if (doneProjectsCodesList.includes(fileNameSplitted[0])) {
    return 'file_name_already_merged';
  }

  if (!fileNameSplitted[1].includes('-')) return 'file_name';

  const secondPartSplitted = fileNameSplitted[1].split('-');
  if (
    secondPartSplitted[0] !== fileNameMap['acr_1'] &&
    secondPartSplitted[0] !== fileNameMap['acr_5']
  ) {
    return 'file_name';
  }

  if (secondPartSplitted[1] !== 'B') return 'file_name';

  return '';
}

function getProjectsCodes(publicationList?: any[]): any[] {
  if (publicationList && publicationList.length > 0) {
    return publicationList.map(item => item.project_code);
  }
  return [];
}

function getDoneProjectsCodes(publicationList?: any[]): any[] {
  if (publicationList && publicationList.length > 0) {
    return publicationList.map(item => {
      if (calcMergeStatus(item) === 'Done') return item.project_code;
    });
  }
  return [];
}

function collectValidFiles(
  newFiles: FileList,
  context: FileValidationContext,
): { files: File[]; error: string; filename: string } {
  const files: File[] = [];
  let error = '';
  let filename = '';

  for (let i = 0; i < newFiles.length; i++) {
    const newFile = newFiles[i];
    filename = newFile?.name;
    error = validateFile(newFile, context);
    if (error) break;
    files.push(newFile);
  }

  return { files, error, filename };
}

function notifyUploadError(err: any) {
  const errorsList = err?.response?.data;
  if (errorsList) {
    showNotification(
      <ACRUploadNotificationError errors={errorsList} />,
      'error',
    );
  } else {
    showNotification('Error on files upload. Please retry later', 'error');
  }
}

const ACRMultiFilesUploadButton = ({ period }: Props) => {
  const [modal, setModal] = useState(false);
  const [filesSelected, setFilesSelected] = useState<File[]>([]);
  const [filesUploading, setFilesUploading] = useState(false);
  const [filesSelectedError, setFilesSelectedError] =
    useState<FilesSelectedError | null>(null);

  const dispatch = useDispatch();

  const publicationList = useSelector(
    (state: RootState) => state.viewData?.ACR_REPORTS_LIST?.data,
  );

  const projectsCodesList = useMemo(
    () => getProjectsCodes(publicationList),
    [publicationList],
  );

  const doneProjectsCodesList = useMemo(
    () => getDoneProjectsCodes(publicationList),
    [publicationList],
  );

  useEffect(() => {
    if (!modal) {
      setFilesSelected([]);
      setFilesSelectedError(null);
      setFilesUploading(false);
    }
  }, [modal]);

  function handleFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = e.target.files;

    if (!newFiles || newFiles.length === 0) return;

    const { files, error, filename } = collectValidFiles(newFiles, {
      projectsCodesList,
      doneProjectsCodesList,
    });

    if (error) {
      setFilesSelectedError({
        error,
        filename,
      });
      return;
    }

    setFilesSelectedError(null);
    setFilesSelected(files);
  }

  async function handleFilesUpload() {
    setFilesUploading(true);

    const formData = new FormData();

    formData.append('multi', String(true));
    formData.append('period', String(period));
    filesSelected.forEach(file => {
      formData.append('files', file);
    });

    const url = getActionAPISource(PDF_MERGE_LIST);

    try {
      const publicationList = await apiCallAsync(url, formData, 'post');

      dispatch(
        receiveAPIResponseHoldingData(
          publicationList.data,
          REPORT_LIST_DATA_KEY,
        ),
      );

      setFilesUploading(false);
      showNotification('Files uploaded successfully', 'success');
      setModal(false);
    } catch (err) {
      console.error(err);
      notifyUploadError(err);
      setFilesUploading(false);
      setModal(false);
    }
  }

  return (
    <>
      <Button
        onClick={() => setModal(true)}
        data-test-id="multiple-file-upload-button"
      >
        Multiple upload
      </Button>
      {modal && (
        <Modal
          className="acr-file-upload-modal"
          open={true}
          hideClose
          modalLabel="Multiple file upload"
          onRequestClose={() => null}
          secondaryButtonText="Cancel"
          onSecondarySubmit={() => setModal(false)}
          secondaryButtonDisabled={false}
          primaryButtonText="Upload"
          onRequestSubmit={handleFilesUpload}
          primaryButtonDisabled={
            filesSelected?.length <= 0 || !!filesSelectedError
          }
        >
          <input
            type="file"
            hidden
            onChange={handleFilesChange}
            multiple
            accept="application/pdf"
            id="acr-multi-upload"
          />
          <label htmlFor="acr-multi-upload" className="acr-upload-label">
            Choose files
          </label>
          {filesUploading && (
            <InlineLoading
              className="acr-file-upload-loader"
              description="Uploading..."
            />
          )}
          {filesSelectedError && (
            <ACRUploadErrorMessage
              filename={filesSelectedError?.filename}
              error={filesSelectedError?.error}
            />
          )}
          {filesSelected?.length > 0 && !filesSelectedError && (
            <div className="acr-file-selected">
              <p>Files selected:</p>
              {filesSelected?.map((item, index) => (
                <div key={`${item.name}-${index}`}>{item?.name}</div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default ACRMultiFilesUploadButton;
